import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, ChevronDown, Clock } from "lucide-react";
import api from "../lib/api";
import { useDashboard } from "../context/DashboardContext";

/* ---------- helpers ---------- */
const G = "#22c55e", Y = "#f59e0b", R = "#ef4444", MUTED = "var(--text-muted)";

const fmtPrice = (n) => {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n);
  const d = a >= 1000 ? 2 : a >= 1 ? 3 : a >= 0.01 ? 5 : 7;
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: d });
};
const fmtNum = (n, d = 2) => (n == null || isNaN(n) ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: d }));

const TERMINAL_BAD = ["INVALIDATED", "STOP_LOSS_HIT", "CANCELLED", "EXPIRED"];

function computeLight(a, sw) {
  if (!a || !a.indicators) return { light: "RED", dir: null, stack: "NONE" };
  const { ema20, ema50, ema200, rsi, atr } = a.indicators;
  const price = a.price;
  const invalidated = sw && TERMINAL_BAD.includes(sw.status);

  let stack = "MIXED", dir = null;
  if (ema200 != null) {
    if (ema20 > ema50 && ema50 > ema200) { stack = "UP"; dir = "LONG"; }
    else if (ema20 < ema50 && ema50 < ema200) { stack = "DOWN"; dir = "SHORT"; }
  } else if (ema20 != null && ema50 != null) {
    if (ema20 > ema50) dir = "LONG"; else if (ema20 < ema50) dir = "SHORT";
    stack = "PARTIAL";
  }
  const aligned = stack === "UP" || stack === "DOWN";
  const rsiExtreme = rsi > 75 || rsi < 25;
  const rsiOk = rsi >= 40 && rsi <= 60;
  const nearOk = atr ? Math.abs(price - ema20) <= atr : false;

  let light;
  if (stack === "MIXED" || stack === "NONE" || rsiExtreme || invalidated) light = "RED";
  else if (aligned && rsiOk && nearOk) light = "GREEN";
  else light = "YELLOW";
  return { light, dir, stack, rsiExtreme, rsiOk, nearOk, invalidated };
}

function computePlan(a, sw, dir) {
  if (sw && sw.entryLow != null && sw.stopLoss != null) {
    const entryMid = (sw.entryLow + sw.entryHigh) / 2;
    return {
      source: "plan", dir: sw.direction, entryLow: sw.entryLow, entryHigh: sw.entryHigh,
      entryMid, sl: sw.stopLoss, tp1: sw.tp1, tp2: sw.tp2, tp3: sw.tp3,
      risk: Math.abs(entryMid - sw.stopLoss),
    };
  }
  if (!a || !dir || !a.indicators) return null;
  const { ema20, ema50, atr } = a.indicators;
  if (ema20 == null || ema50 == null || !atr) return null;
  const long = dir === "LONG";
  const entryMid = ema20;
  const sl = long ? ema50 - 0.3 * atr : ema50 + 0.3 * atr;
  const risk = Math.abs(entryMid - sl);
  const t = (m) => (long ? entryMid + m * risk : entryMid - m * risk);
  return {
    source: "hitung", dir, entryLow: ema20 - 0.3 * atr, entryHigh: ema20 + 0.3 * atr,
    entryMid, sl, tp1: t(1.5), tp2: t(2.5), tp3: t(3.5), risk,
  };
}

const statusText = (sw) => {
  if (!sw) return "Belum Ada Setup";
  switch (sw.status) {
    case "INVALIDATED": return "Invalidated";
    case "STOP_LOSS_HIT": return "Stop Loss Tersentuh";
    case "CANCELLED": return "Dibatalkan";
    case "EXPIRED": return "Kedaluwarsa";
    case "COMPLETED": return "Selesai · 100%";
    case "TP2_HIT": return "TP2 Tercapai · 67%";
    case "TP1_HIT": return "TP1 Tercapai · 33%";
    case "ACTIVE_RUNNING": return "Menuju TP1";
    case "ENTRY_ZONE_TOUCHED": return "Harga di Zona Entry";
    case "MISSED_ENTRY": return "Entry Terlewat";
    case "WAITING_FOR_ENTRY": return "Menunggu Entry";
    default: return "Menunggu Entry";
  }
};
const barPct = (sw) => {
  if (!sw) return 0;
  if (sw.status === "COMPLETED") return 100;
  if (sw.status === "TP2_HIT") return 67;
  if (sw.status === "TP1_HIT") return 33;
  if (sw.status === "ACTIVE_RUNNING") return 15;
  return 0;
};

const rsiChipColor = (r) => (r > 70 || r < 30 ? R : r >= 40 && r <= 60 ? G : Y);

function emaDesc(a) {
  const { ema20, ema50, ema200 } = a.indicators;
  if (ema200 == null)
    return `EMA Stack: ── Campur — EMA 200 belum tersedia (data 4H belum cukup). Tren belum bisa dipastikan.`;
  if (ema20 > ema50 && ema50 > ema200)
    return `EMA Stack: ▲ Naik Berurutan — EMA 20 (${fmtPrice(ema20)}) di atas EMA 50 (${fmtPrice(ema50)}) di atas EMA 200 (${fmtPrice(ema200)}). Tren 4 jam naik.`;
  if (ema20 < ema50 && ema50 < ema200)
    return `EMA Stack: ▼ Turun Berurutan — EMA 20 (${fmtPrice(ema20)}) di bawah EMA 50 (${fmtPrice(ema50)}) di bawah EMA 200 (${fmtPrice(ema200)}). Tren 4 jam turun.`;
  return `EMA Stack: ── Campur — EMA 20 (${fmtPrice(ema20)}), EMA 50 (${fmtPrice(ema50)}), EMA 200 (${fmtPrice(ema200)}) tidak berurutan. Tren belum jelas, hindari entry.`;
}
function rsiDescText(r) {
  if (r < 25) return "Sangat oversold. Kemungkinan pantulan naik.";
  if (r < 40) return "Momentum jual. Hati-hati untuk long.";
  if (r <= 60) return "Netral. Masih ada ruang bergerak ke dua arah.";
  if (r <= 75) return "Momentum beli. Hati-hati untuk short.";
  return "Sangat overbought. Kemungkinan koreksi turun.";
}
function priceDesc(a) {
  const { ema20, atr } = a.indicators;
  const p = a.price;
  const dist = Math.abs(p - ema20);
  const mult = atr ? dist / atr : null;
  let ket = "ATR belum tersedia.";
  if (mult != null) {
    if (mult <= 0.5) ket = "Di zona pullback ideal.";
    else if (mult <= 1) ket = "Mendekati batas aman. Masih bisa entry dengan SL ketat.";
    else ket = "Terlalu jauh. Tunggu harga kembali mendekati EMA20.";
  }
  return `Posisi Harga: ${fmtPrice(p)} — ${fmtPrice(dist)} poin dari EMA20 (${fmtPrice(ema20)}). ${ket}`;
}
function suggestionText(light, sw, plan) {
  if (sw) {
    const s = sw.status;
    const mid = (sw.entryLow + sw.entryHigh) / 2;
    if (["INVALIDATED", "CANCELLED", "EXPIRED"].includes(s)) return "Rencana dibatalkan. Jangan entry. Tunggu setup baru.";
    if (s === "STOP_LOSS_HIT") return "Stop loss tersentuh. Rencana selesai. Tunggu setup baru.";
    if (s === "COMPLETED") return "Semua target tercapai. Rencana selesai.";
    if (s === "TP2_HIT") return `Geser SL ke TP1 (${fmtPrice(sw.tp1)}). Sisa posisi menuju TP3.`;
    if (s === "TP1_HIT") return `Geser SL ke breakeven (${fmtPrice(mid)}). Biarkan menuju TP2.`;
    if (s === "ACTIVE_RUNNING") return `Tunggu. SL tetap di ${fmtPrice(sw.stopLoss)}.`;
    if (s === "WAITING_FOR_ENTRY" || s === "ENTRY_ZONE_TOUCHED")
      return `Siapkan order di zona ${fmtPrice(sw.entryLow)}–${fmtPrice(sw.entryHigh)}. Pasang SL di ${fmtPrice(sw.stopLoss)}.`;
  }
  if (light === "GREEN" && plan) return `Siapkan order di zona ${fmtPrice(plan.entryLow)}–${fmtPrice(plan.entryHigh)}. Pasang SL di ${fmtPrice(plan.sl)}.`;
  if (light === "YELLOW") return "Belum waktunya entry. Pantau kondisi yang belum terpenuhi (RSI atau jarak harga ke EMA20).";
  return "Tidak ada setup. Jangan buka posisi.";
}

const LIGHT_META = {
  GREEN: { c: G, t: "Layak Entry", dot: "🟢" },
  YELLOW: { c: Y, t: "Pantau", dot: "🟡" },
  RED: { c: R, t: "Jangan Entry", dot: "🔴" },
};

/* ---------- small UI ---------- */
const Panel = ({ children, style }) => (
  <div style={{ background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 10, padding: 12, ...style }}>{children}</div>
);
const KV = ({ k, v, c }) => (
  <div className="flex items-center justify-between py-[3px]">
    <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{k}</span>
    <span className="text-[12px] font-semibold" style={{ color: c || "var(--text)" }}>{v}</span>
  </div>
);

/* ---------- card ---------- */
function PairCard({ symbol, a, sw, livePrice, capital, riskPct }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(false);

  const L = useMemo(() => computeLight(a, sw), [a, sw]);
  const dir = sw?.direction || L.dir;
  const plan = useMemo(() => computePlan(a, sw, L.dir), [a, sw, L.dir]);
  const price = livePrice ?? a?.price;

  const size = plan && plan.risk > 0 ? (capital * (riskPct / 100)) / plan.risk : null;
  const notional = size != null ? size * plan.entryMid : null;

  const lm = LIGHT_META[L.light];
  const emaChip = L.stack === "UP" ? { s: "▲▲▲", t: "Naik", c: G }
    : L.stack === "DOWN" ? { s: "▼▼▼", t: "Turun", c: R }
    : { s: "──", t: "Campur", c: MUTED };
  const rsi = a?.indicators?.rsi;
  const showPlan = plan && (L.light === "GREEN" || L.light === "YELLOW" || sw);

  const targets = sw?.progress?.targets || [];
  const tState = (n) => targets.find((t) => t.tp === n)?.state;
  const nextT = targets.find((t) => t.state === "NEXT");
  const activated = !!sw?.activatedAt || ["ACTIVE_RUNNING", "TP1_HIT", "TP2_HIT", "COMPLETED", "STOP_LOSS_HIT"].includes(sw?.status);
  const stopped = sw && TERMINAL_BAD.includes(sw.status);
  const mileIcon = (st) => (st === "done" ? "✓" : st === "fail" ? "✗" : "○");
  const mileColor = (st) => (st === "done" ? G : st === "fail" ? R : MUTED);
  const tpMile = (n) => (tState(n) === "HIT" ? "done" : stopped ? "fail" : "pending");
  const milestones = sw ? [
    { k: "CREATED", st: "done" },
    { k: "ENTRY", st: activated ? "done" : stopped ? "fail" : "pending" },
    { k: "TP1", st: tpMile(1) }, { k: "TP2", st: tpMile(2) }, { k: "TP3", st: tpMile(3) },
  ] : [];

  return (
    <div
      data-testid={`watch-card-${symbol}`}
      style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", marginBottom: 12 }}
    >
      {/* collapsed rows */}
      <button
        data-testid={`watch-card-toggle-${symbol}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left"
        style={{ padding: 14, borderLeft: `4px solid ${lm.c}` }}
      >
        {/* baris 1 */}
        <div className="flex items-center justify-between">
          <span className="text-[15px] font-extrabold tracking-tight">{symbol}</span>
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-bold tabular-nums">{price != null ? `$${fmtPrice(price)}` : "—"}</span>
            {dir && (
              <span className="text-[10px] font-extrabold px-2 py-[3px] rounded-md"
                style={{ background: dir === "LONG" ? "rgba(34,197,94,.16)" : "rgba(239,68,68,.16)", color: dir === "LONG" ? G : R }}>
                {dir}
              </span>
            )}
            <ChevronDown size={16} style={{ color: "var(--text-muted)", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
          </div>
        </div>
        {/* baris 2 */}
        <div className="mt-2">
          <div className="h-[6px] rounded-full overflow-hidden" style={{ background: "var(--panel-2)" }}>
            <div style={{ width: `${barPct(sw)}%`, height: "100%", background: lm.c, transition: "width .3s" }} />
          </div>
          <div className="text-[11px] mt-1 font-medium" style={{ color: "var(--text-dim)" }}>{statusText(sw)}</div>
        </div>
        {/* baris 3 */}
        <div className="flex items-center gap-3 mt-2 text-[11px] font-semibold">
          <span style={{ color: emaChip.c }}>{emaChip.s} {emaChip.t}</span>
          <span style={{ color: rsi != null ? rsiChipColor(rsi) : MUTED }}>RSI {rsi != null ? fmtNum(rsi, 1) : "—"}</span>
          <span className="ml-auto flex items-center gap-1" style={{ color: lm.c }}>
            <span style={{ fontSize: 9 }}>{lm.dot}</span> {lm.t}
          </span>
        </div>
      </button>

      {/* drawer */}
      <div style={{ maxHeight: open ? 2200 : 0, overflow: "hidden", transition: "max-height .35s ease" }}>
        <div style={{ padding: 14, paddingTop: 0 }} className="space-y-3">
          {/* A. kotak lampu besar */}
          <div data-testid={`watch-light-${symbol}`} style={{ borderRadius: 10, padding: 12, background: `${lm.c}1a`, border: `1px solid ${lm.c}55` }}>
            <div className="flex items-center gap-2 mb-2">
              <span style={{ width: 14, height: 14, borderRadius: 999, background: lm.c, display: "inline-block" }} />
              <span className="text-[13px] font-extrabold" style={{ color: lm.c }}>{lm.t}</span>
            </div>
            {a ? (
              <div className="space-y-2 text-[11px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
                <div>{emaDesc(a)}</div>
                <div>RSI: {fmtNum(rsi, 1)} — {rsiDescText(rsi)}</div>
                <div>{priceDesc(a)}</div>
              </div>
            ) : (
              <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Data 4H tidak tersedia dari provider.</div>
            )}
          </div>

          {/* B. rencana trade */}
          {showPlan && (
            <Panel>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-bold">Rencana Trade</span>
                <span className="text-[9px] px-1.5 py-[2px] rounded" style={{ background: "var(--panel)", color: "var(--text-muted)" }}>
                  {plan.source === "plan" ? "Dari rencana aktif" : "Dihitung dari EMA/ATR"}
                </span>
              </div>
              <KV k="Arah" v={plan.dir} c={plan.dir === "LONG" ? G : R} />
              <KV k="Zona Entry" v={`${fmtPrice(plan.entryLow)} – ${fmtPrice(plan.entryHigh)}`} c="#8b5cf6" />
              <KV k="Stop Loss" v={fmtPrice(plan.sl)} c={R} />
              <KV k="TP1" v={fmtPrice(plan.tp1)} c={G} />
              <KV k="TP2" v={fmtPrice(plan.tp2)} c={G} />
              <KV k="TP3" v={fmtPrice(plan.tp3)} c={G} />
              <div style={{ height: 1, background: "var(--border)", margin: "6px 0" }} />
              <KV k="Risiko per unit" v={`$${fmtPrice(plan.risk)}`} />
              <KV k="Ukuran Posisi" v={size != null ? `${fmtNum(size, 4)} ${symbol.replace("USDT", "")}` : "—"} c={G} />
              <KV k="Nilai Posisi" v={notional != null ? `$${fmtNum(notional, 2)}` : "—"} c="var(--accent-soft)" />
            </Panel>
          )}

          {/* C. progress tracker */}
          {sw && (
            <Panel>
              <div className="text-[12px] font-bold mb-2">Progress</div>
              <div className="flex items-center justify-between">
                {milestones.map((m, idx) => (
                  <React.Fragment key={m.k}>
                    <div className="flex flex-col items-center" style={{ minWidth: 44 }}>
                      <span className="grid place-items-center rounded-full text-[11px] font-bold"
                        style={{ width: 22, height: 22, background: "var(--panel)", border: `1.5px solid ${mileColor(m.st)}`, color: mileColor(m.st) }}>
                        {mileIcon(m.st)}
                      </span>
                      <span className="text-[8px] mt-1" style={{ color: "var(--text-muted)" }}>{m.k}</span>
                    </div>
                    {idx < milestones.length - 1 && <div style={{ flex: 1, height: 2, background: "var(--border)" }} />}
                  </React.Fragment>
                ))}
              </div>
              {nextT && (
                <div className="text-[10px] mt-2" style={{ color: "var(--text-dim)" }}>
                  Selanjutnya: TP{nextT.tp} di {fmtPrice(nextT.target)} (masih {fmtPrice(nextT.distance)} poin
                  {nextT.distancePct != null ? ` / ${fmtNum(nextT.distancePct, 2)}%` : ""})
                </div>
              )}
            </Panel>
          )}

          {/* D. saran otomatis */}
          <div data-testid={`watch-suggest-${symbol}`} style={{ borderRadius: 10, padding: 12, background: "rgba(139,92,246,.10)", border: "1px solid rgba(139,92,246,.28)" }}>
            <div className="text-[11px] font-bold mb-1" style={{ color: "#a78bfa" }}>Saran</div>
            <div className="text-[11px] leading-relaxed" style={{ color: "var(--text-dim)" }}>{suggestionText(L.light, sw, plan)}</div>
          </div>

          {/* E. detail teknikal */}
          <div>
            <button onClick={() => setDetail((d) => !d)} data-testid={`watch-detail-toggle-${symbol}`}
              className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
              <ChevronDown size={12} style={{ transform: detail ? "rotate(180deg)" : "none", transition: "transform .2s" }} /> Lihat Detail
            </button>
            {detail && (
              <div className="mt-2 space-y-2 text-[11px]" style={{ color: "var(--text-dim)" }}>
                {sw?.score && (
                  <Panel>
                    <div className="font-semibold mb-1">Skor Setup: {sw.score.total}/{sw.score.max}</div>
                    {(sw.reasoning || []).map((r, i) => (
                      <div key={i} className="flex gap-1"><span style={{ color: "var(--text-muted)" }}>·</span><span>{r}</span></div>
                    ))}
                  </Panel>
                )}
                {sw && (
                  <Panel>
                    <div><b style={{ color: "var(--text)" }}>Pemicu Entry:</b> {sw.entryTrigger}</div>
                    <div className="mt-1"><b style={{ color: R }}>Invalidasi:</b> {sw.invalidationRule}</div>
                    <div className="mt-1" style={{ color: "var(--text-muted)" }}>
                      Dibuat {sw.createdAt} · Berlaku sampai {sw.validUntil} · Source 4H {sw.sourceCandleClose}
                    </div>
                  </Panel>
                )}
                {!sw && <div style={{ color: "var(--text-muted)" }}>Belum ada rencana swing aktif untuk pair ini.</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- page ---------- */
export default function Watchlist() {
  const { pairs, livePrices } = useDashboard();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [capital, setCapital] = useState(() => Number(localStorage.getItem("ym_watchlist_capital")) || 100);
  const [riskPct, setRiskPct] = useState(() => Number(localStorage.getItem("ym_watchlist_risk")) || 1);

  useEffect(() => { localStorage.setItem("ym_watchlist_capital", String(capital)); }, [capital]);
  useEffect(() => { localStorage.setItem("ym_watchlist_risk", String(riskPct)); }, [riskPct]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const load = useCallback(async () => {
    if (!pairs?.length) { setLoading(false); return; }
    const entries = await Promise.all(pairs.map(async (p) => {
      try {
        const [a, sw] = await Promise.all([api.getAnalysis(p.symbol, "4h"), api.getSwing(p.symbol)]);
        return [p.symbol, { a, sw: sw?.active || null }];
      } catch (e) { return [p.symbol, { a: null, sw: null }]; }
    }));
    setData(Object.fromEntries(entries));
    setLoading(false);
  }, [pairs]);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const wita = now.toLocaleTimeString("id-ID", { hour12: false, timeZone: "Asia/Makassar" });

  return (
    <div style={{ maxWidth: 480, margin: "0 auto" }}>
      {/* header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye size={18} style={{ color: "var(--accent-soft)" }} />
            <h1 className="text-[17px] font-extrabold tracking-tight">YUMAERA Watchlist</h1>
          </div>
          <span className="flex items-center gap-1 text-[11px] font-semibold tabular-nums" style={{ color: "var(--text-muted)" }}>
            <Clock size={12} /> {wita} WITA
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>Modal (USDT)</span>
            <input data-testid="watch-capital-input" type="number" value={capital}
              onChange={(e) => setCapital(Math.max(0, Number(e.target.value)))}
              className="text-[13px] font-semibold rounded-lg px-3 py-2 outline-none"
              style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>Risiko (%)</span>
            <input data-testid="watch-risk-input" type="number" step="0.1" value={riskPct}
              onChange={(e) => setRiskPct(Math.max(0, Number(e.target.value)))}
              className="text-[13px] font-semibold rounded-lg px-3 py-2 outline-none"
              style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
          </label>
        </div>
      </div>

      {/* list */}
      {loading ? (
        <div className="text-[12px] py-10 text-center" style={{ color: "var(--text-muted)" }}>Memuat data pasar…</div>
      ) : !pairs?.length ? (
        <div className="text-[12px] py-10 text-center" style={{ color: "var(--text-muted)" }}>Belum ada pair. Tambahkan pair di Pusat Komando terlebih dahulu.</div>
      ) : (
        pairs.map((p) => {
          const d = data[p.symbol] || {};
          return (
            <PairCard key={p.symbol} symbol={p.symbol} a={d.a} sw={d.sw}
              livePrice={livePrices?.[p.symbol]?.price} capital={capital} riskPct={riskPct} />
          );
        })
      )}
    </div>
  );
}
